import type React from 'react';

import {
  DeviceOrientationProvider,
  TranslationsProvider,
} from '@mfw/components';

import { AppVersionCheck } from '../allFeatures/AppVersion';
import { BrandingLoader } from '../components/BrandingLoader';
import TenantGuard from '../pages/Guards/TenantGuard/TenantGuard';
import { ThemeProvider as MuiThemeProvider } from '../theme';
import OrganizationProvider from './OrganizationProvider';
import UploadWorkerProvider from './UploadWorkerProvider';
import PermissionsProvider from './PermissionsProvider';
import { SnackbarProvider } from './SnackbarProvider';
import NavigationProvider from './NavigationProvider';
import { AppThemeProvider } from './AppThemeProvider';
import { ManifestProvider } from './ManifestProvider';
import ApiClientProvider from './ApiClientProvider';
import UserLocaleSync from './UserLocaleSync';
import TenantProvider from './TenantProvider';
import AuthProvider from './AuthProvider';

/**
 * THE SPLIT (PR-2.1): one identity owner across the `/auth` ↔ `/` boundary.
 *
 * `/auth/*` and `/` used to be two top-level routes, each with its own copy of this whole
 * stack — so every login, corp password change and accept-invite navigation MOUNTED A
 * SECOND `AuthProvider`. A fresh one has no `currentUser` of its own, its
 * `onIdTokenChanged` observer fires on subscribe with the persisted Firebase user, and §3
 * then makes it mint: one redundant Bearer `/auth/me` per navigation out of the gate,
 * measured live (docs/review/auth-1.md I0).
 *
 * So the stack is cut in two at the identity line:
 *
 *   `AppIdentityProviders` — everything the SESSION itself hangs off (tenant, theme,
 *   snackbar, the auth owner). The router renders exactly one of these above both
 *   branches, so it survives the navigation and nothing re-boots.
 *
 *   `AppSessionProviders` — the account-scoped providers. These stay ROUTE-LOCAL BY
 *   DESIGN: `routes.tsx` renders one per branch with an explicit `key`, which preserves
 *   the reset on `/auth ↔ /` the two separate trees used to give for free. Deliberately
 *   not keyed by `currentUser.id` at an ancestor of the page — an anonymous login would
 *   remount `AcceptInvitePage`, which then re-checks a hash it has already spent.
 *
 * `AppProviders` composes both, unchanged, for the consumers that render one self-
 * contained tree and navigate nowhere: the anonymous public page, `/privacyPolicy`, the
 * terms-of-use confirmation and the wildcard.
 */
export interface AppProvidersProps {
  children: React.ReactNode;
  /**
   * See `ManifestProvider`'s own prop doc. Defaults to `true` — every route
   * in the app proper may offer the install prompt; the anonymous public
   * page (`publicSiteRoutes.tsx`) is the one that opts out.
   */
  canPromptAppInstall?: boolean;
  /**
   * See `UserLocaleSync`'s own prop doc. Defaults to `true` — the signed-in
   * account's language decides everywhere except the public page, whose
   * language belongs to the page.
   */
  canApplyAccountLocale?: boolean;
}

export type AppIdentityProvidersProps = Omit<
  AppProvidersProps,
  'canPromptAppInstall'
>;

export type AppSessionProvidersProps = Omit<
  AppProvidersProps,
  'canApplyAccountLocale'
> & {
  /**
   * See `ManifestProvider`'s own prop doc. Defaults to `true`; the `/auth`
   * branch passes `false` — it is replaced right after sign-in or onboarding.
   */
  canShowAppInstallPrompt?: boolean;
};

/**
 * The identity shell. ONE per router tree — see the split note above.
 */
export const AppIdentityProviders = ({
  canApplyAccountLocale = true,
  children,
}: AppIdentityProvidersProps) => {
  return (
    <TranslationsProvider>
      <ApiClientProvider>
        <TenantProvider>
          <AppThemeProvider>
            <MuiThemeProvider>
              <SnackbarProvider>
                <BrandingLoader />
                <AppVersionCheck />
                <AuthProvider>
                  <UserLocaleSync isEnabled={canApplyAccountLocale} />
                  {/* Under TenantProvider, the only place it can read the lookup:
                      a page's first request must carry this domain's tenant. */}
                  <TenantGuard>{children}</TenantGuard>
                </AuthProvider>
              </SnackbarProvider>
            </MuiThemeProvider>
          </AppThemeProvider>
        </TenantProvider>
      </ApiClientProvider>
    </TranslationsProvider>
  );
};

/**
 * The account-scoped providers. Route-local, and keyed by the branch that renders them.
 */
export const AppSessionProviders = ({
  canPromptAppInstall = true,
  canShowAppInstallPrompt = true,
  children,
}: AppSessionProvidersProps) => {
  return (
    <PermissionsProvider>
      <OrganizationProvider>
        <NavigationProvider>
          <UploadWorkerProvider>
            <ManifestProvider
              canPromptAppInstall={canPromptAppInstall}
              canShowAppInstallPrompt={canShowAppInstallPrompt}
            />
            <DeviceOrientationProvider>{children}</DeviceOrientationProvider>
          </UploadWorkerProvider>
        </NavigationProvider>
      </OrganizationProvider>
    </PermissionsProvider>
  );
};

const AppProviders = ({
  canApplyAccountLocale = true,
  canPromptAppInstall = true,
  children,
}: AppProvidersProps) => {
  return (
    <AppIdentityProviders canApplyAccountLocale={canApplyAccountLocale}>
      <AppSessionProviders canPromptAppInstall={canPromptAppInstall}>
        {children}
      </AppSessionProviders>
    </AppIdentityProviders>
  );
};

export default AppProviders;
